import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useAct, gsap, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   ÖZELLİKLER — sekmeli, tam animasyonlu (Framer tarzı)
   Sekmeye basınca: görsel clip-path ile açılıyor, metinler
   sırayla (stagger) giriyor, aktif sekmenin çizgisi kayıyor
   ve altındaki çubuk otomatik ilerlemeyi dolduruyor. Dolunca
   bir sonrakine geçiyor; üstüne gelince duruyor.
   ══════════════════════════════════════════════════════ */

const FEATURES = [
  {
    n: '01', tab: 'Uydu ve Tarla Analizi', title: 'Tarlanın durumunu harita üzerinden gör',
    desc: 'Uydu verileri ve analiz katmanlarıyla tarlanı farklı açılardan incele. Dikkat çeken bölgeleri belirle ve saha kontrolünü daha bilinçli yap.',
    img: '/assets/gallery/lovic1.webp',
  },
  {
    n: '02', tab: 'Hastalık Analizi', title: 'Bir yaprağı analiz etmek birkaç saniye sürer',
    desc: 'Fotoğrafı yükle, Agren AI\'nin görsel analizini incele ve olası sorunları daha iyi anlamaya çalış.',
    img: '/assets/gallery/lovic3.webp',
  },
  {
    n: '03', tab: 'Tarım Asistanı', title: 'Tarım hakkında aklına takılanı sor',
    desc: 'Agren AI ile tarımla ilgili sorularını konuşarak ilerlet. Tek bir cevapla bitirmek yerine, konu hakkında yeni sorular sor.',
    img: '/assets/gallery/lovic4.webp',
  },
  {
    n: '04', tab: 'Tarla Yönetimi', title: 'Tarlanı sadece bugün değil, süreç boyunca takip et',
    desc: 'Tarla bilgilerini, görevlerini ve üretim sürecindeki önemli noktaları tek yerde düzenle.',
    img: '/assets/gallery/lovic2.webp',
  },
];

const AUTO_MS = 4600;

export default function FeatureTabs() {
  const [active, setActive] = useState(0);
  const [ind, setInd] = useState({ left: 0, width: 0 });
  const tabRefs = useRef([]);
  const panelRefs = useRef([]);
  const fillRef = useRef(null);
  const progressRef = useRef(null);
  const pausedRef = useRef(false);
  const prevRef = useRef(0);

  /* aktif sekmenin altındaki çizgiyi konumla */
  const place = useCallback(() => {
    const el = tabRefs.current[active];
    if (el) setInd({ left: el.offsetLeft, width: el.offsetWidth });
  }, [active]);

  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const on = () => place();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [place]);

  /* panel geçiş animasyonu — boyamadan önce (flaş olmasın) */
  useLayoutEffect(() => {
    const panel = panelRefs.current[active];
    const dir = active >= prevRef.current ? 1 : -1;
    prevRef.current = active;
    if (!panel) return;

    if (prefersReduced()) {
      gsap.set(panel.querySelectorAll('.fp-img, .fp-anim'), { clearProps: 'all', opacity: 1 });
      return;
    }
    const img = panel.querySelector('.fp-img');
    const texts = panel.querySelectorAll('.fp-anim');
    const tl = gsap.timeline();
    tl.fromTo(img,
      { clipPath: dir > 0 ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)', scale: 1.14 },
      { clipPath: 'inset(0% 0% 0% 0%)', scale: 1, duration: 0.72, ease: 'power3.out' }, 0);
    tl.fromTo(texts,
      { y: 26, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.55, stagger: 0.08, ease: 'power3.out' }, 0.12);
  }, [active]);

  /* otomatik ilerleme çubuğu — dolunca sonrakine geçer */
  useEffect(() => {
    if (prefersReduced()) return undefined;
    const fill = fillRef.current;
    if (!fill) return undefined;
    gsap.set(fill, { scaleX: 0, transformOrigin: '0% 50%' });
    const tw = gsap.to(fill, {
      scaleX: 1, duration: AUTO_MS / 1000, ease: 'none',
      onComplete: () => setActive((a) => (a + 1) % FEATURES.length),
    });
    progressRef.current = tw;
    if (pausedRef.current) tw.pause();
    return () => tw.kill();
  }, [active]);

  const hoverPause = (p) => {
    pausedRef.current = p;
    const tw = progressRef.current;
    if (tw) { if (p) tw.pause(); else tw.resume(); }
  };

  /* girişte yumuşak beliriş */
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    if (prefersReduced()) return;
    gsap.from(q('.ft-reveal'), {
      opacity: 0, y: 34, ease: 'power3.out', duration: 0.7, stagger: 0.12,
      scrollTrigger: { trigger: root, start: 'top 72%' },
    });
  }, []);

  return (
    <section
      ref={ref}
      id="ozellikler"
      className="relative overflow-hidden"
      style={{ background: '#050605' }}
      onMouseEnter={() => hoverPause(true)}
      onMouseLeave={() => hoverPause(false)}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 18%, rgba(200,255,34,.08) 0%, rgba(200,255,34,0) 60%)' }} />

      <div className="relative max-w-[1120px] mx-auto px-6 md:px-10 py-[clamp(84px,14vh,150px)]">
        <div className="ft-reveal text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-1.5 font-mono text-[11.5px] tracking-[.2em] uppercase"
            style={{ color: '#C8FF22' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8FF22', boxShadow: '0 0 10px #C8FF22' }} />
            Özellikler
          </span>
          <h2 className="mt-6 font-display font-bold text-white leading-[1.05] tracking-[-0.03em] mx-auto max-w-[18ch]"
            style={{ fontSize: 'clamp(28px,3.6vw,48px)' }}>
            Tek uygulamada, tarlanın farklı katmanları
          </h2>
        </div>

        {/* sekmeler + kayan gösterge + otomatik ilerleme */}
        <div className="ft-reveal relative mt-[clamp(36px,5vh,60px)]">
          <div className="flex flex-wrap justify-center gap-x-1 gap-y-2 border-b border-white/10">
            {FEATURES.map((f, i) => (
              <button
                key={f.tab}
                ref={(el) => { tabRefs.current[i] = el; }}
                onClick={() => setActive(i)}
                className="relative px-4 md:px-5 py-3 text-[13.5px] md:text-[15px] font-medium transition-colors duration-300"
                style={{ color: i === active ? '#EAF7C8' : 'rgba(247,247,242,.5)' }}
                aria-selected={i === active}
              >
                <span className="mr-2 font-mono text-[11px]" style={{ color: i === active ? '#C8FF22' : 'rgba(200,255,34,.35)' }}>
                  {f.n}
                </span>
                {f.tab}
              </button>
            ))}
          </div>
          {/* kayan çubuk: içindeki dolgu otomatik ilerlemeyi gösterir */}
          <div className="absolute -bottom-px h-[2px] rounded-full overflow-hidden"
            style={{ left: ind.left, width: ind.width, background: 'rgba(200,255,34,.18)', transition: 'left .45s cubic-bezier(.4,0,.2,1), width .45s cubic-bezier(.4,0,.2,1)' }}>
            <div ref={fillRef} className="h-full w-full rounded-full"
              style={{ background: 'linear-gradient(90deg,#C8FF22,#A8C24A)', boxShadow: '0 0 12px rgba(200,255,34,.5)', transformOrigin: '0% 50%' }} />
          </div>
        </div>

        {/* panel: 5 katman üst üste, aktif olan görünür */}
        <div className="ft-reveal relative mt-[clamp(36px,5vh,64px)] grid">
          {FEATURES.map((f, i) => {
            const on = i === active;
            return (
              <div key={f.tab}
                ref={(el) => { panelRefs.current[i] = el; }}
                className="col-start-1 row-start-1 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center"
                style={{ opacity: on ? 1 : 0, pointerEvents: on ? 'auto' : 'none', transition: 'opacity .3s ease', zIndex: on ? 2 : 1 }}
                aria-hidden={!on}
              >
                <div className="order-2 md:order-1">
                  <div className="fp-anim font-mono text-[13px] tracking-[.18em]" style={{ color: '#C8FF22' }}>
                    {f.n} / 04
                  </div>
                  <h3 className="fp-anim mt-4 font-display font-bold text-white leading-[1.08] tracking-[-0.03em]"
                    style={{ fontSize: 'clamp(26px,3.2vw,42px)' }}>
                    {f.title}
                  </h3>
                  <p className="fp-anim mt-5 text-[15.5px] leading-relaxed text-white/60 max-w-[46ch]">
                    {f.desc}
                  </p>
                </div>

                <div className="order-1 md:order-2">
                  <div className="relative rounded-[24px] overflow-hidden border border-white/10 mx-auto"
                    style={{ aspectRatio: '1 / 1', maxWidth: 'min(460px,72vw)', boxShadow: '0 30px 70px rgba(0,0,0,.5)' }}>
                    <div className="absolute top-0 left-0 right-0 h-px z-10" aria-hidden="true"
                      style={{ background: 'linear-gradient(90deg, transparent, rgba(200,255,34,.55), transparent)' }} />
                    <img src={f.img} alt={f.title} loading="lazy" decoding="async"
                      className="fp-img w-full h-full object-cover" style={{ willChange: 'clip-path, transform' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
